import { RangeRef, useSlateSelector } from 'domains/reporter/RichTextEditor/core';
import { getActivePicklist } from '../../utils/selection';
import { PicklistPluginElement } from '../../slate-custom-types';
import { useCallback, useEffect, useRef } from 'react';
import { Editor, Range } from 'slate';
import type { NodeEntry } from 'domains/reporter/RichTextEditor/core';

type UseTriggerlessPicklistStateProps = {
  editor: Editor;
};

const getSelectionPicklistId = (
  editor: Editor,
  selection: Nullable<Range | RangeRef['current']>
): string | null => {
  if (selection == null) {
    return null;
  }

  const picklistEntry = getActivePicklist(
    editor,
    selection
  ) as NodeEntry<PicklistPluginElement> | null;

  return picklistEntry?.[0].picklistID ?? null;
};

export const useTriggerlessPicklistState = ({ editor }: UseTriggerlessPicklistStateProps) => {
  const stateRef = useRef<{
    isAllowed: boolean;
    lastSelectionPicklistId: string | null;
  }>({
    isAllowed: false,
    lastSelectionPicklistId: null,
  });
  const picklistIdAtSelection = useSlateSelector((slateEditor) =>
    getSelectionPicklistId(slateEditor as Editor, slateEditor.selection)
  );

  const syncFromPicklistId = useCallback((selectionPicklistId: string | null) => {
    const state = stateRef.current;
    if (selectionPicklistId === state.lastSelectionPicklistId) {
      return;
    }

    state.lastSelectionPicklistId = selectionPicklistId;
    state.isAllowed = selectionPicklistId != null;
  }, []);

  const refreshTriggerlessPicklistStateFromSelection = useCallback(
    (selection: Nullable<Range | RangeRef['current']>) => {
      if (selection == null && stateRef.current.lastSelectionPicklistId == null) {
        return;
      }

      syncFromPicklistId(getSelectionPicklistId(editor, selection));
    },
    [editor, syncFromPicklistId]
  );

  useEffect(() => {
    syncFromPicklistId(picklistIdAtSelection);
  }, [picklistIdAtSelection, syncFromPicklistId]);

  useEffect(() => {
    refreshTriggerlessPicklistStateFromSelection(editor.selection);
  }, [editor.selection, refreshTriggerlessPicklistStateFromSelection]);

  const getPicklistIdForSelection = useCallback(
    (selection: Nullable<Range | RangeRef['current']>) => getSelectionPicklistId(editor, selection),
    [editor]
  );

  const isTriggerlessAllowedForPicklist = useCallback(
    (picklistId: Nullable<string>) =>
      picklistId != null &&
      stateRef.current.isAllowed &&
      picklistId === stateRef.current.lastSelectionPicklistId,
    []
  );

  const consumeTriggerlessPicklistChoice = useCallback((picklistId: Nullable<string>) => {
    if (picklistId !== stateRef.current.lastSelectionPicklistId) {
      return;
    }

    stateRef.current.isAllowed = false;
  }, []);

  return {
    isTriggerlessAllowedForPicklist,
    consumeTriggerlessPicklistChoice,
    getPicklistIdForSelection,
    refreshTriggerlessPicklistStateFromSelection,
  };
};
